import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import { WithStyles, createStyles, withStyles } from '@material-ui/core';

import { ApplicationTrustedDomainValue } from 'yeself-common/typings/application/Application';

const styles = createStyles({
    listItem: {
        width: '90%',
        '& span': {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        },
    },
});

const isTrustedDomainsValue = (value: unknown): value is ApplicationTrustedDomainValue => !!value;

interface TrustedDomainsFormProps extends WithStyles<typeof styles> {
    initialValue: ApplicationTrustedDomainValue[];
    onChange: (value: ApplicationTrustedDomainValue[]) => void;
}

const TrustedDomainsForm = ({
    initialValue = [],
    onChange,
    classes,
}: TrustedDomainsFormProps): React.ReactElement => {
    const intl = useIntl();

    const [value, setValue] = useState('');
    const [list, setList] = useState(initialValue);

    const handleOnChange = useCallback((event) => {
        setValue(event.target.value);
    }, []);

    const handleOnAdd = useCallback(() => {
        if (isTrustedDomainsValue(value)) {
            const newList = [value, ...list];

            setList(newList);

            onChange(newList);
        }

        setValue('');
    }, [list, onChange, value]);

    const handleOnDelete = useCallback(
        (indexToDelete) => () => {
            const newList = list.filter((_, index) => index !== indexToDelete);

            setList(newList);
            onChange(newList);
        },
        [list, onChange]
    );

    const validateDomainRegexp = /^[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
    const domainRegExp = new RegExp(validateDomainRegexp);

    const error = value !== '' && !domainRegExp.test(value);

    return (
        <Grid container>
            <Grid item xs={12} style={{ marginTop: '2.5rem' }}>
                <Typography variant="subtitle1" style={{ padding: '0px 0px 12px 0px' }} gutterBottom>
                    <FormattedMessage
                        id="Settings.general.trustedDomains"
                        defaultMessage="Trusted Domains"
                    />
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Grid container direction="row" alignItems="center">
                    <Grid id="yeself-textField" item xs={8}>
                        <TextField
                            fullWidth
                            type="text"
                            value={value}
                            onChange={handleOnChange}
                            helperText={error && intl.formatMessage({ id: 'Settings.general.label4' })}
                            error={error}
                            placeholder={intl.formatMessage({ id: 'Settings.general.label3' })}
                        />
                    </Grid>
                    <Grid item xs={4} container justify="flex-end">
                        <Button
                            id="yeself-button"
                            variant="contained"
                            color="primary"
                            onClick={handleOnAdd}
                            disabled={!value}
                        >
                            <FormattedMessage id="Buttons.add" defaultMessage="Add" />
                        </Button>
                    </Grid>
                    <Typography variant="caption">
                        <FormattedMessage
                            id="Settings.general.trustedDomains.message"
                            defaultMessage="* Updating trusted domains might take up to few minutes to have an effect."
                        />
                    </Typography>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <List>
                    {list.map((domain, index) => (
                        <ListItem key={domain} disableGutters className={classes.listItem}>
                            <ListItemText primary={domain} title={domain}/>
                            <ListItemSecondaryAction>
                                <IconButton aria-label="Delete" onClick={handleOnDelete(index)}>
                                    <CancelPresentationIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(TrustedDomainsForm);
